import { ScaleInfo, ScaleDetector } from './scaleDetection';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  scaleInfo: ScaleInfo;
  sampleAnalysis: {
    totalRows: number;
    validResponses: number;
    invalidResponses: number;
    averageBeforeProcessing: number;
    averageAfterProcessing: number;
  };
}

export class DataValidator {
  /**
   * التحقق الشامل من البيانات قبل التحليل
   */
  static validateAnalysisData(
    data: any[],
    workshopColumn: string,
    questionColumns: string[]
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // 1. التحقق الأساسي
    if (!data || data.length === 0) {
      errors.push('لا توجد بيانات للتحليل');
    }
    
    if (!workshopColumn) {
      errors.push('لم يتم تحديد عمود الورشة');
    }
    
    if (!questionColumns || questionColumns.length === 0) {
      errors.push('لا توجد أعمدة أسئلة للتحليل');
    }
    
    // 2. اكتشاف المقياس
    const scaleInfo = ScaleDetector.detectScale(data, questionColumns);
    
    if (scaleInfo.confidence < 0.5) {
      warnings.push(`ثقة منخفضة في اكتشاف المقياس (${(scaleInfo.confidence * 100).toFixed(1)}%)`);
    }
    
    // 3. تحليل عينة من البيانات
    const sampleAnalysis = this.analyzeSample(data, questionColumns, scaleInfo);
    
    // 4. التحقق من التناسق
    if (sampleAnalysis.invalidResponses > sampleAnalysis.validResponses * 0.1) {
      warnings.push(`نسبة عالية من الاستجابات غير الصالحة (${sampleAnalysis.invalidResponses}/${sampleAnalysis.totalRows})`);
    }
    
    // 5. التحقق من الورش
    const workshopValidation = this.validateWorkshops(data, workshopColumn);
    errors.push(...workshopValidation.errors);
    warnings.push(...workshopValidation.warnings);
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      scaleInfo,
      sampleAnalysis
    };
  }
  
  /**
   * تحليل عينة من البيانات
   */
  private static analyzeSample(
    data: any[],
    questionColumns: string[],
    scaleInfo: ScaleInfo
  ) {
    let validResponses = 0;
    let invalidResponses = 0;
    const allValues: number[] = [];
    
    for (const row of data.slice(0, 100)) { // عينة من 100 صف
      for (const column of questionColumns) {
        const value = row[column];
        const validatedValue = ScaleDetector.validateValue(value, scaleInfo);
        
        if (validatedValue !== null) {
          validResponses++;
          allValues.push(validatedValue);
        } else {
          invalidResponses++;
        }
      }
    }
    
    const averageBeforeProcessing = allValues.length > 0 
      ? allValues.reduce((sum, val) => sum + val, 0) / allValues.length 
      : 0;
    
    return {
      totalRows: Math.min(100, data.length),
      validResponses,
      invalidResponses,
      averageBeforeProcessing,
      averageAfterProcessing: averageBeforeProcessing // سيتم تحديثه بعد المعالجة
    };
  }
  
  /**
   * التحقق من صحة بيانات الورش
   */
  private static validateWorkshops(data: any[], workshopColumn: string) {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    const workshopValues = data.map(row => row[workshopColumn]).filter(v => v != null && v !== '');
    const uniqueWorkshops = new Set(workshopValues);
    
    if (uniqueWorkshops.size === 0) {
      errors.push('لا توجد ورش عمل صالحة في البيانات');
    } else if (uniqueWorkshops.size === 1) {
      warnings.push('يوجد ورشة عمل واحدة فقط - لن تكون المقارنات مفيدة');
    }
    
    // التحقق من توزيع البيانات على الورش
    const workshopCounts = Array.from(uniqueWorkshops).map(workshop => {
      const count = workshopValues.filter(v => v === workshop).length;
      return { workshop, count };
    });
    
    const minCount = Math.min(...workshopCounts.map(w => w.count));
    const maxCount = Math.max(...workshopCounts.map(w => w.count));
    
    if (minCount < 5) {
      warnings.push(`بعض الورش لديها استجابات قليلة جداً (أقل من 5)`);
    }
    
    if (maxCount / minCount > 10) {
      warnings.push(`توزيع غير متوازن للاستجابات بين الورش`);
    }
    
    return { errors, warnings };
  }
  
  /**
   * مقارنة النتائج قبل وبعد المعالجة للتأكد من عدم تدمير البيانات
   */
  static compareResults(
    originalData: any[],
    processedResults: any,
    questionColumns: string[],
    scaleInfo: ScaleInfo
  ): { isConsistent: boolean; issues: string[] } {
    const issues: string[] = [];
    
    // حساب المتوسط الأصلي
    const originalValues: number[] = [];
    for (const row of originalData) {
      for (const column of questionColumns) {
        const value = ScaleDetector.validateValue(row[column], scaleInfo);
        if (value !== null) {
          originalValues.push(value);
        }
      }
    }
    
    const originalAverage = originalValues.length > 0 
      ? originalValues.reduce((sum, val) => sum + val, 0) / originalValues.length 
      : 0;
    
    // مقارنة مع النتائج المعالجة
    const processedAverage = processedResults.overall?.average || 0;
    const difference = Math.abs(originalAverage - processedAverage);
    const percentageDifference = originalAverage > 0 ? (difference / originalAverage) * 100 : 0;
    
    console.log('🔍 مقارنة النتائج:', {
      originalAverage: originalAverage.toFixed(3),
      processedAverage: processedAverage.toFixed(3),
      difference: difference.toFixed(3),
      percentageDifference: percentageDifference.toFixed(1) + '%'
    });
    
    if (percentageDifference > 10) {
      issues.push(`اختلاف كبير في المتوسطات: ${percentageDifference.toFixed(1)}%`);
    }
    
    if (processedAverage > scaleInfo.max || processedAverage < scaleInfo.min) {
      issues.push(`المتوسط المعالج خارج نطاق المقياس المتوقع`);
    }
    
    return {
      isConsistent: issues.length === 0,
      issues
    };
  }
} 